// AxiosInterceptor.js

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import axiosInstance from '../../configure/apiConfig';
import { useWebsocket } from '../websocket';
import { Set } from 'immutable';
import { useErrorHandler } from '../ErrorBoundary';
import { getWithLocalExpiry } from '@utils/index';

const AxiosInterceptorContext = createContext({});

const AxiosInterceptor = ({ children }) => {
  const { sendNotification } = useWebsocket(); // Access WebSocket context
  const requestedActions = useRef({})
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [stackedAPIs, setStackAPIs] = useState([])
  const { setError } = useErrorHandler();
  useEffect(()=>{
    if(stackedAPIs.length > 0 ){
      if(!isButtonDisabled) return setIsButtonDisabled(true);
    }else{
      setIsButtonDisabled(false)
    }
  },[stackedAPIs])

  useEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (config) => {
        const { url, headers } = config;
        const actionHeaderKey = "Socket-Action";
        console.log(headers, "headers in axios interceptor");
        console.log(url, "url in axios interceptor");
        // Only proceed if the main action header is present
        if (headers && headers[actionHeaderKey]) {
          // Track unique URLs
          setStackAPIs((prev) => [...new Set([...prev, url])]);

          const customPrefix = "Socket-";
          const customPayload = {};

          Object.keys(headers).forEach((key) => {
            if (key.startsWith(customPrefix)) {
              const prop = key
                .replace(customPrefix, "")
                .replace(/-./g, (x) => x[1].toUpperCase()); // PascalCase if you want, or use x[1].toLowerCase() for camelCase
              customPayload[prop.charAt(0).toLowerCase() + prop.slice(1)] =
                headers[key]; // convert first char to lowercase
              delete headers[key];
            }
          });

          // Save the custom payload to the tracker
          if (Object.keys(customPayload).length > 0) {
            requestedActions.current[url] = customPayload;
          }
        }

        // Always attach auth token
        const token = getWithLocalExpiry();
        headers["Authorization"] = `Bearer ${token}`;

        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
        (response) => {
            const { status, config } = response;
      
            setStackAPIs(pre => [...pre.filter(url => url !== config.url)]);

            if (status === 200) {
              // call a function here
              if (requestedActions.current[config.url]) {
                // If the action is an object, extract the action property
                if (typeof requestedActions.current[config.url] !== 'object') return response;
                  const action = requestedActions.current[config.url].action;
                  // If the dealId is present, you can use it as needed
                  const dealId = requestedActions.current[config.url].dealId;
                  const idType = requestedActions.current[config.url].idType;
                  const dealStatus = requestedActions.current[config.url].dealStatus;
                  const workbookId = requestedActions.current[config.url].workbookId;
                  const demandId = requestedActions.current[config.url].demandId;

                try {
                  if(action){
                    sendNotification(action,dealId,idType,dealStatus,workbookId,demandId);
                  }
                } catch (error) {
                  console.error(error)
                }
                delete requestedActions.current[config.url];
              }
            }
            return response;
          },
          (error) => {
            if(error.code === "ERR_CANCELED") return Promise.reject(error);

            setError(error);

            setStackAPIs(pre => [...pre.filter(url => url !== error.config.url)]);
          
            return Promise.reject(error); // Don't swallow the error
          }
    );

    // Clean up interceptors on unmount
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [sendNotification]);

  return (
    <AxiosInterceptorContext.Provider value={{isButtonDisabled}}>
      {children}
    </AxiosInterceptorContext.Provider>
  );
};

export { axiosInstance };

export const useAxiosContext = () => useContext(AxiosInterceptorContext);

export default AxiosInterceptor;

